import { BrandKit, VoiceParams } from 'types'
import { PropsWithChildren, useEffect, useState } from 'react'
import { useUpdateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useUpdateBrandVoice'
import { useCreateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useCreateBrandVoice'
import Styles from './BrandVoice.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Text } from 'blaze-ui/Text/Text'
import { ArrayInput } from 'blaze-ui/ArrayInput/ArrayInput'

type EditableVoiceParam = keyof Omit<VoiceParams, 'name'>

type Props = {
  brandKit: BrandKit
}

interface LabelProps {
  htmlFor?: string
}

export const Label = ({ children, ...props }: PropsWithChildren<LabelProps>) => (
  <label className={Styles.label} {...props}>
    <Text variant="metadata" color="var(--dark-80)">
      {children}
    </Text>
  </label>
)

interface TagInputProps {
  value: string | string[]
  name: string
  placeholder: string
  onChange: (value: Set<string>) => void
}

const TagInput = ({ name, value, placeholder, onChange }: TagInputProps) => {
  const arrayValue = Array.isArray(value) ? new Set(value) : new Set([value])

  return (
    <ArrayInput
      value={arrayValue}
      onChange={onChange}
      name={name}
      placeholder={placeholder}
      chipClassName={Styles.chip}
    />
  )
}

export const BrandVoice = ({ brandKit }: Props) => {
  const { brand_voice: brandVoice } = brandKit
  const initialVoiceParams = brandVoice?.voice_params ?? {
    name: `${brandKit.name} Brand Voice`,
    purpose: [],
    audience: [],
    character: [],
    emotions: [],
    language: [],
    syntax: [],
    tone: [],
  }

  const [voice, setVoice] = useState<VoiceParams>(initialVoiceParams)
  const { mutateAsync: updateBrandVoice } = useUpdateBrandVoice({ id: brandVoice?.id ?? 0 })
  const { mutateAsync: createBrandVoice } = useCreateBrandVoice()

  const stringParams = JSON.stringify(brandVoice?.voice_params)

  useEffect(() => {
    // sync external changes
    brandVoice?.voice_params && setVoice(brandVoice.voice_params)
  }, [stringParams]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleParamChange =
    (k: EditableVoiceParam) =>
    (tags: string[]): VoiceParams => {
      const newVoice = { ...voice, [k]: tags }
      setVoice(newVoice)
      return newVoice
    }

  const handleUpdate = async (newVoice: VoiceParams) => {
    const dirty = !brandVoice || JSON.stringify(newVoice) !== JSON.stringify(initialVoiceParams)

    if (!dirty) {
      return
    }

    if (brandVoice?.id) {
      updateBrandVoice({ voice_params: newVoice })
    } else {
      createBrandVoice({ voiceParams: newVoice, brandKitId: brandKit.id })
    }
  }

  return (
    <div className={Styles.brandVoice}>
      <div className={Styles.parameter}>
        <Label htmlFor="purpose">Purpose</Label>
        <Input
          size="sm"
          isTextarea
          id="purpose"
          name="name"
          value={voice['purpose'].join(', ')}
          className={Styles.textbox}
          onChange={(e) => handleParamChange('purpose')([e.currentTarget.value])}
          onBlur={() => {
            handleUpdate(voice)
          }}
        />
      </div>
      <div className={Styles.parameter}>
        <Label htmlFor="audience">Audience</Label>
        <Input
          size="sm"
          isTextarea
          id="audience"
          name="name"
          value={voice['audience'].join(', ')}
          className={Styles.textbox}
          onChange={(e) => handleParamChange('audience')([e.currentTarget.value])}
          onBlur={() => {
            handleUpdate(voice)
          }}
        />
      </div>
      <div className={Styles.parameter}>
        <Label>Tone</Label>
        <TagInput
          value={voice['tone']}
          name="Tone"
          placeholder="Relaxed, Formal, Friendly"
          onChange={(value) => {
            const newVoice = handleParamChange('tone')(Array.from(value))
            handleUpdate(newVoice)
          }}
        />
      </div>
      <div className={Styles.parameter}>
        <Label>Emotions</Label>
        <TagInput
          value={voice['emotions']}
          name="Emotion"
          placeholder="Positive, Optimistic, Supportive"
          onChange={(value) => {
            const newVoice = handleParamChange('emotions')(Array.from(value))
            handleUpdate(newVoice)
          }}
        />
      </div>
      <div className={Styles.parameter}>
        <Label>Character</Label>
        <TagInput
          value={voice['character']}
          name="Character"
          placeholder="Professional, Playful, Authoritative"
          onChange={(value) => {
            const newVoice = handleParamChange('character')(Array.from(value))
            handleUpdate(newVoice)
          }}
        />
      </div>
      <div className={Styles.parameter}>
        <Label>Syntax</Label>
        <TagInput
          value={voice['syntax']}
          name="Syntax"
          placeholder="Short, Use Contractions, Concise"
          onChange={(value) => {
            const newVoice = handleParamChange('syntax')(Array.from(value))
            handleUpdate(newVoice)
          }}
        />
      </div>
      <div key="language" className={Styles.parameter}>
        <Label>Language</Label>
        <TagInput
          value={voice['language']}
          name="Language"
          placeholder="Simple, Jargon-free, Technical"
          onChange={(value) => {
            const newVoice = handleParamChange('language')(Array.from(value))
            handleUpdate(newVoice)
          }}
        />
      </div>
    </div>
  )
}
